import { useCallback } from 'react';

import { useParams } from 'react-router';

import { useCurrentUser, useUserData } from '@app-velocity/libs/auth';
import { MessageBus } from '@app-velocity/message-bus';
import {
  useGetTeamQuery,
  useGetTeamsQuery,
  useProjectsQuery,
} from '@app-velocity/libs/teams-data-access';

const messageBus = new MessageBus('users');

export const useCurrentTeamPermissions = () => {
  const { userId } = useUserData();
  const { teamId } = useParams<{ teamId: string }>();
  const { data: team } = useGetTeamQuery(teamId || '');
  const { data: userTeams } = useGetTeamsQuery();

  const currentUser = useCurrentUser();

  const { data: workspaces } = useProjectsQuery(userId);

  const getFirstPaidTeamAvailable = () => {
    const team = userTeams?.find((t) => t.subscriptionTier !== 'FREE');

    if (!team) return null;

    const workspace = workspaces?.find((w) => w.teamId === team.teamId);

    if (!workspace) return null;

    return {
      ...team,
      workspaceId: workspace.id,
    };
  };

  const canRunAppraisals = useCallback(() => {
    if (!currentUser) return false;
    if (!team || !userTeams) return false;

    const userTeam = userTeams.find((t) => t.teamId === teamId);
    if (!userTeam) return false;
    const { subscriptionTier: tier, appraisalsLeft } = userTeam;

    if (!tier) return false;

    const normalizedAppraisalsLeft = appraisalsLeft || 0;

    switch (tier) {
      case 'FREE': {
        if (normalizedAppraisalsLeft >= 5) {
          messageBus.publish('modal:higher-plan-feature-only:open');

          return false;
        }

        return true;
      }

      default:
        return true;
    }
  }, [currentUser, team, userTeams, teamId]);

  const getHigherTieredTeam = useCallback(() => {
    if (!currentUser) return false;
    if (!team || !userTeams) return false;
    const userTeam = userTeams.find((t) => t.teamId === teamId);
    if (!userTeam) return false;
    const { subscriptionTier: tier, userRole } = userTeam;

    if (!tier || !userRole || tier !== 'FREE') return false;

    const availablePaidTeam = getFirstPaidTeamAvailable();

    return availablePaidTeam;
  }, [currentUser, userTeams, teamId, team, getFirstPaidTeamAvailable]);

  const canRunSimulations = useCallback(
    (amount: number) => {
      if (!currentUser) return false;
      if (!team || !userTeams) return false;

      const userTeam = userTeams.find((t) => t.teamId === teamId);
      if (!userTeam) return false;
      const { subscriptionTier: tier, userRole, simulationLimit } = userTeam;

      if (!tier || !userRole) return false;

      const simulationsLeft = simulationLimit || 0;

      switch (tier) {
        case 'FREE': {
          if (amount > 1) {
            messageBus.publish('modal:higher-plan-feature-only:open');

            return false;
          }

          if (simulationsLeft >= 1) return true;

          messageBus.publish('modal:no-more-credits:open', {
            role: userRole,
            tier,
            teamId: team.id!,
          });

          return false;
        }

        default: {
          if (simulationsLeft >= amount) return true;
          messageBus.publish('modal:no-more-credits:open', {
            role: userRole,
            tier,
            teamId: team.id!,
          });

          return false;
        }
      }
    },
    [currentUser, team, userTeams, teamId]
  );

  const canRunNotDemos = useCallback(() => {
    if (!currentUser) return false;
    if (!team || !userTeams) return false;

    const userTeam = userTeams.find((t) => t.teamId === teamId);
    if (!userTeam) return false;
    const { subscriptionTier: tier, userRole } = userTeam;

    if (!tier || !userRole) return false;

    return tier !== 'FREE';
  }, [currentUser, team, userTeams, teamId]);

  if (!currentUser || !team || !userTeams || !team.subscriptionTier)
    return null;

  return {
    canRunAppraisals,
    canRunSimulations,
    canRunNotDemos,
    getHigherTieredTeam,
  };
};
